import Box from '@risk-smart/themed-cloudscape-components/box';
import Table from '@risksmart-app/components/src/table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CustomPdfFormValues } from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';
import { useExportOptions } from 'src/hooks/useExportOptions';

import type { TablePropsWithActions, TableRecord } from '@/utils/table/types';

import { toExportFilename } from '../../../../utils/exportFilename';
import styles from '../../style.module.scss';
import { useDashboardBulkExportStore } from '../../useDashboardBulkExportStore';
import { useWidgetContext } from '../../widget-context/WidgetContext';

type Props<T extends TableRecord> = {
  loading?: boolean;
  tableProps: TablePropsWithActions<T>;
};

const TableWidget = <T extends TableRecord>({
  tableProps,
  loading,
}: Props<T>) => {
  const widgetData = useWidgetContext();
  const { addWidgetExport, removeWidgetExport } = useDashboardBulkExportStore();
  const [showModal, setShowModal] = useState(false);
  const entityLabel = 'Widget Table';

  // Capture the CSV builder in a ref so the bulk-export function always reads
  // the latest table data without forcing the registration effect to re-run on
  // every render (tableProps changes identity each render).
  const exportToCsvStringRef = useRef(tableProps.exportToCsvString);
  exportToCsvStringRef.current = tableProps.exportToCsvString;

  const onShowCustomPdfModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const getActiveRibbonFilters = useCallback(() => [], []);

  const { exportOptions, exportToPdf } = useExportOptions<T>({
    tableProps,
    pdfTemplateId: undefined,
    entityLabel,
    getActiveRibbonFilters,
    onShowCustomPdfModal,
  });

  // Capture as ref to ensure we can grab latest export options after table has fully loaded
  // without an infinite re-render
  const exportOptionsRef = useRef(exportOptions);
  exportOptionsRef.current = exportOptions;

  const stableExportFns = useMemo(
    () =>
      exportOptions.map((option, index) => ({
        id: option.id,
        text: option.text,
        fn: () => {
          exportOptionsRef.current[index]?.exportFunction();
        },
      })),
    // The dependency array ensures this only re-runs if the IDs of the export options change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(exportOptions.map((o) => o.id))]
  );

  useEffect(() => {
    if (widgetData && !loading) {
      widgetData?.setExportFns?.(stableExportFns);
    }
  }, [loading, stableExportFns, widgetData]);

  // Register a CSV contribution for the full-dashboard zip export so table
  // widgets appear alongside the chart images (RSP-6025).
  const widgetId = widgetData?.widgetId;
  const widgetTitle = widgetData?.title;
  useEffect(() => {
    if (!widgetId || loading) {
      return;
    }
    addWidgetExport({
      id: widgetId,
      filename: toExportFilename(widgetTitle),
      exportFn: async () => ({
        kind: 'csv',
        csv: exportToCsvStringRef.current(),
      }),
    });

    return () => removeWidgetExport(widgetId);
  }, [widgetId, widgetTitle, loading, addWidgetExport, removeWidgetExport]);

  return (
    <Box padding={{ left: 's', right: 's' }}>
      <div className={styles.stickyHeader}>
        <Table
          {...tableProps}
          loading={loading}
          variant={'borderless'}
          stickyHeader={true}
        />
      </div>
      <CustomPdfExportModal
        exportToPdf={async (options: CustomPdfFormValues) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={entityLabel}
        showModal={showModal}
        setShowModal={setShowModal}
        showRibbonOptions={true}
      />
    </Box>
  );
};

export default TableWidget;
